/**
 * The global error boundary: if a screen breaks, the person sees plain words, a way back, and a
 * "Copy details" button whose text is the same bundle Admin → Health's Copy diagnostic makes (the fixed
 * preamble, the error, the page's last requests) — never a blank screen, never a stack in the page itself.
 *
 * 1.5.13 §1c.5 — one kind of break is not a fault: after a deploy the old bundle asks for a lazy chunk that no
 * longer exists. The first time, the page reloads by itself (the new version loads) and says so instead of the
 * error screen; a second failure straight after is shown as the error it is (lib/chunk-reload.ts keeps the guard).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import i18n from '@/i18n';
import { diagnosticMarkdown } from '@shared/diagnostic';
import { copyText } from '@/lib/clipboard';
import { screenErrorBundle } from '@/lib/screen-error';
import { autoReloadOnce, canAutoReload, isChunkLoadError } from '@/lib/chunk-reload';

interface State {
  error: Error | null;
  stack: string;
  copied: boolean | null;
  /** A chunk that no longer exists, and the page may still reload by itself: no error screen. */
  reloading: boolean;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, stack: '', copied: null, reloading: false };
  private stuck: number | undefined;

  componentWillUnmount(): void {
    if (this.stuck) window.clearTimeout(this.stuck);
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, copied: null, reloading: isChunkLoadError(error) && canAutoReload() };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // the reload is asked for here, not while rendering; if it is refused after all, the error screen shows —
    // and so it does if the page is somehow still here ten seconds later (nobody is left on "Getting the new version…")
    if (isChunkLoadError(error) && this.state.reloading) {
      if (!autoReloadOnce()) this.setState({ reloading: false });
      else this.stuck = window.setTimeout(() => this.setState({ reloading: false }), 10_000);
    }
    this.setState({ stack: info.componentStack ?? '' });
  }

  private copy = async () => {
    if (!this.state.error) return;
    const ok = await copyText(diagnosticMarkdown(screenErrorBundle(this.state.error, this.state.stack, i18n.language, { app: __APP_RELEASE__, sha: __APP_VERSION__, builtAt: __BUILD_TIME__ })));
    this.setState({ copied: ok });
  };

  render(): ReactNode {
    const { error, copied, reloading } = this.state;
    if (!error) return this.props.children;
    const t = i18n.t.bind(i18n);
    if (reloading) {
      return (
        <main id="main" className="error-screen" data-testid="chunk-reloading">
          <p className="help" role="status" aria-live="polite">
            {t('pwa.updating')}
          </p>
        </main>
      );
    }
    return (
      <main id="main" className="error-screen" data-testid="error-boundary">
        <div className="card-signin" role="alert">
          <h1>{t('errors.screenTitle')}</h1>
          <p>{t('errors.screenBody')}</p>
          <div className="row" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button type="button" className="btn" onClick={() => window.location.reload()}>
              {t('errors.reload')}
            </button>
            <a className="btn secondary" href="/">
              {t('errors.goToday')}
            </a>
            <button type="button" className="btn secondary" onClick={() => void this.copy()} data-testid="error-copy">
              {t('errors.copyDetails')}
            </button>
          </div>
          <p className="help" role="status" aria-live="polite">
            {copied === true ? t('errors.copied') : copied === false ? t('errors.copyFailed') : t('errors.copyHelp')}
          </p>
        </div>
      </main>
    );
  }
}
